import React, { useEffect, useState } from "react";
import { enqueueSnackbar } from "notistack";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { Badge } from "../../../components/ui/badge";
import Input from "../../../components/Input";
import { InputText } from "../../../components/InputText";
import { Label } from "../../../components/ui/label";
import { TextArea } from "../../../components/TextArea";
import { SelectSingle } from "../../../components/SelectSingle";
import { MilitarSelectField } from "../../../components/MilitarSelectField";
import { api } from "../../../services/api";
import { usePermissions, PERMISSOES } from "../../../hooks/usePermissions";
import {
  Award,
  Search,
  ArrowUp,
  ArrowDown,
  Plus,
  Edit,
  Trash2,
  Medal,
} from "lucide-react";

interface Militar {
  id: string;
  nick: string;
  patente: {
    id: string;
    patente: string;
    abreviacao: string;
  };
}

interface MilitarMedalha {
  id: string;
  militar: Militar;
  militarPromoveu: Militar;
  criado_em: string;
}

interface MedalhaTipo {
  id: string;
  tipo: string;
  descricao: string | null;
  img: string;
  ordem_tipo: number;
  militares: MilitarMedalha[];
}

interface MedalhaComTipos {
  id: string;
  medalha: string;
  img: string;
  descricao: string | null;
  ordem: number;
  tipos: MedalhaTipo[];
}

interface Medalha {
  id: string;
  medalha: string;
  img: string;
  descricao?: string;
  ordem: number;
}

interface MedalhaTipoSimples {
  id: string;
  idMedalha: string;
  tipo: string;
  descricao?: string;
  img: string;
  ordemTipo: number;
  medalha: {
    id: string;
    medalha: string;
  };
}

export function GerenciarMedalhas() {
  const { hasPermission } = usePermissions();
  const [activeTab, setActiveTab] = useState<"medalhas" | "tipos">("medalhas");
  const [loading, setLoading] = useState(false);

  // Tab Medalhas (com militares)
  const [medallhasComTipos, setMedalhasComTipos] = useState<MedalhaComTipos[]>([]);
  const [searchMedalha, setSearchMedalha] = useState("");
  const [searchNick, setSearchNick] = useState("");

  // Tab Tipos de Medalhas
  const [medalhas, setMedalhas] = useState<Medalha[]>([]);
  const [medalhaTipos, setMedalhaTipos] = useState<MedalhaTipoSimples[]>([]);
  const [filteredTipos, setFilteredTipos] = useState<MedalhaTipoSimples[]>([]);
  const [searchTipo, setSearchTipo] = useState("");
  const [isModalMedalhaOpen, setIsModalMedalhaOpen] = useState(false);
  const [editingMedalha, setEditingMedalha] = useState<Medalha | null>(null);
  const [formNomeMedalha, setFormNomeMedalha] = useState("");
  const [formImgMedalha, setFormImgMedalha] = useState("");
  const [formDescricaoMedalha, setFormDescricaoMedalha] = useState("");

  // Modal Tipo de Medalha
  const [isModalTipoOpen, setIsModalTipoOpen] = useState(false);
  const [editingTipo, setEditingTipo] = useState<MedalhaTipoSimples | null>(null);
  const [formMedalhaIdTipo, setFormMedalhaIdTipo] = useState("");
  const [formNomeTipo, setFormNomeTipo] = useState("");
  const [formImgTipo, setFormImgTipo] = useState("");
  const [formDescricaoTipo, setFormDescricaoTipo] = useState("");

  // Modal Atribuir Medalha
  const [isAtribuirModalOpen, setIsAtribuirModalOpen] = useState(false);
  const [selectedMilitarId, setSelectedMilitarId] = useState("");
  const [selectedMilitarNick, setSelectedMilitarNick] = useState("");
  const [selectedMedalhaId, setSelectedMedalhaId] = useState("");
  const [selectedTipoId, setSelectedTipoId] = useState("");
  const [tiposDisponiveis, setTiposDisponiveis] = useState<MedalhaTipoSimples[]>([]);

  // Modal Remover Medalha do Militar
  const [isRemoverModalOpen, setIsRemoverModalOpen] = useState(false);
  const [militarMedalhaToRemove, setMilitarMedalhaToRemove] = useState<{
    id: string;
    militarNick: string;
    medalhaNome: string;
  } | null>(null);

  // Modal Excluir Medalha
  const [isExcluirMedalhaModalOpen, setIsExcluirMedalhaModalOpen] = useState(false);
  const [medalhaToDelete, setMedalhaToDelete] = useState<{
    id: string;
    medalha: string;
  } | null>(null);

  // Modal Excluir Tipo
  const [isExcluirTipoModalOpen, setIsExcluirTipoModalOpen] = useState(false);
  const [tipoToDelete, setTipoToDelete] = useState<{
    id: string;
    tipo: string;
  } | null>(null);

  useEffect(() => {
    if (activeTab === "medalhas") {
      loadMedalhasComMilitares();
      // Carregar lista de medalhas e tipos para modal
      if (medalhas.length === 0) {
        loadMedalhas();
      }
      if (medalhaTipos.length === 0) {
        loadMedalhaTipos();
      }
    } else {
      loadMedalhas();
      loadMedalhaTipos();
    }
  }, [activeTab]);

  useEffect(() => {
    if (!searchTipo.trim()) {
      setFilteredTipos(medalhaTipos);
    } else {
      const filtered = medalhaTipos.filter((t) =>
        t.tipo.toLowerCase().includes(searchTipo.toLowerCase()) ||
        t.medalha.medalha.toLowerCase().includes(searchTipo.toLowerCase())
      );
      setFilteredTipos(filtered);
    }
  }, [searchTipo, medalhaTipos]);

  useEffect(() => {
    if (selectedMedalhaId && medalhaTipos.length > 0) {
      const tipos = medalhaTipos.filter((t) => t.idMedalha === selectedMedalhaId);
      setTiposDisponiveis(tipos);
    } else {
      setTiposDisponiveis([]);
    }
  }, [selectedMedalhaId, medalhaTipos]);

  const loadMedalhasComMilitares = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (searchMedalha) params.append("medalha", searchMedalha);
      if (searchNick) params.append("nick", searchNick);

      const response = await api.get(`/medalhas/militares?${params.toString()}`);
      setMedalhasComTipos(response.data);
    } catch (error) {
      enqueueSnackbar("Erro ao carregar medalhas", { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  const loadMedalhas = async (showLoading = true) => {
    try {
      if (showLoading) setLoading(true);
      const response = await api.get("/medalhas");
      setMedalhas(response.data);
    } catch (error) {
      enqueueSnackbar("Erro ao carregar medalhas", { variant: "error" });
    } finally {
      if (showLoading) setLoading(false);
    }
  };

  const loadMedalhaTipos = async (showLoading = true) => {
    try {
      if (showLoading) setLoading(true);
      const response = await api.get("/medalhas/tipos");
      const tiposComIdMedalha = response.data.map((tipo: any) => ({
        ...tipo,
        idMedalha: tipo.medalha?.id || "",
      }));
      setMedalhaTipos(tiposComIdMedalha);
      setFilteredTipos(tiposComIdMedalha);
    } catch (error) {
      enqueueSnackbar("Erro ao carregar tipos de medalhas", { variant: "error" });
    } finally {
      if (showLoading) setLoading(false);
    }
  };

  const handleSearchMedalhas = (e: React.FormEvent) => {
    e.preventDefault();
    loadMedalhasComMilitares();
  };

  const moveMedalha = async (index: number, direction: "up" | "down") => {
    if (
      (direction === "up" && index === 0) ||
      (direction === "down" && index === medalhas.length - 1)
    ) {
      return;
    }

    const newIndex = direction === "up" ? index - 1 : index + 1;
    const newMedalhas = [...medalhas];
    const [removed] = newMedalhas.splice(index, 1);
    newMedalhas.splice(newIndex, 0, removed);

    const medalhasAtualizadas = newMedalhas.map((m, idx) => ({
      ...m,
      ordem: idx + 1,
    }));

    setMedalhas(medalhasAtualizadas);

    try {
      const ordens = medalhasAtualizadas.map((m) => ({
        id: m.id,
        ordem: m.ordem,
      }));

      await api.patch("/medalhas/reordenar", { medalhas: ordens });
      enqueueSnackbar("Ordem atualizada com sucesso", { variant: "success" });
    } catch (error) {
      enqueueSnackbar("Erro ao atualizar ordem", { variant: "error" });
      await loadMedalhas(false);
    }
  };

  // Medalha CRUD
  const openCreateMedalhaModal = () => {
    setEditingMedalha(null);
    setFormNomeMedalha("");
    setFormImgMedalha("");
    setFormDescricaoMedalha("");
    setIsModalMedalhaOpen(true);
  };

  const openEditMedalhaModal = (medalha: Medalha) => {
    setEditingMedalha(medalha);
    setFormNomeMedalha(medalha.medalha);
    setFormImgMedalha(medalha.img);
    setFormDescricaoMedalha(medalha.descricao || "");
    setIsModalMedalhaOpen(true);
  };

  const closeMedalhaModal = () => {
    setIsModalMedalhaOpen(false);
    setEditingMedalha(null);
    setFormNomeMedalha("");
    setFormImgMedalha("");
    setFormDescricaoMedalha("");
  };

  const handleSaveMedalha = async () => {
    if (!formNomeMedalha || !formImgMedalha) {
      enqueueSnackbar("Preencha todos os campos obrigatórios", { variant: "warning" });
      return;
    }

    try {
      setLoading(true);

      if (editingMedalha) {
        await api.put(`/medalhas/${editingMedalha.id}`, {
          medalha: formNomeMedalha,
          img: formImgMedalha,
          descricao: formDescricaoMedalha,
        });
        enqueueSnackbar("Medalha atualizada com sucesso", { variant: "success" });
      } else {
        await api.post("/medalhas", {
          medalha: formNomeMedalha,
          img: formImgMedalha,
          descricao: formDescricaoMedalha,
        });
        enqueueSnackbar("Medalha criada com sucesso", { variant: "success" });
      }

      closeMedalhaModal();
      loadMedalhas();
    } catch (error: any) {
      enqueueSnackbar(
        error.response?.data?.message || "Erro ao salvar medalha",
        { variant: "error" }
      );
    } finally {
      setLoading(false);
    }
  };

  const openExcluirMedalhaModal = (id: string, nome: string) => {
    setMedalhaToDelete({ id, medalha: nome });
    setIsExcluirMedalhaModalOpen(true);
  };

  const closeExcluirMedalhaModal = () => {
    setIsExcluirMedalhaModalOpen(false);
    setMedalhaToDelete(null);
  };

  const handleDeleteMedalha = async () => {
    if (!medalhaToDelete) return;

    try {
      await api.delete(`/medalhas/${medalhaToDelete.id}`);
      enqueueSnackbar("Medalha excluída com sucesso", { variant: "success" });
      closeExcluirMedalhaModal();
      loadMedalhas();
      loadMedalhaTipos(); // Recarregar tipos pois CASCADE deleta também
    } catch (error: any) {
      enqueueSnackbar(
        error.response?.data?.message || "Erro ao excluir medalha",
        { variant: "error" }
      );
    }
  };

  // Tipo CRUD
  const openCreateTipoModal = () => {
    setEditingTipo(null);
    setFormMedalhaIdTipo("");
    setFormNomeTipo("");
    setFormImgTipo("");
    setFormDescricaoTipo("");
    setIsModalTipoOpen(true);
  };

  const openEditTipoModal = (tipo: MedalhaTipoSimples) => {
    setEditingTipo(tipo);
    setFormMedalhaIdTipo(tipo.idMedalha);
    setFormNomeTipo(tipo.tipo);
    setFormImgTipo(tipo.img);
    setFormDescricaoTipo(tipo.descricao || "");
    setIsModalTipoOpen(true);
  };

  const closeTipoModal = () => {
    setIsModalTipoOpen(false);
    setEditingTipo(null);
    setFormMedalhaIdTipo("");
    setFormNomeTipo("");
    setFormImgTipo("");
    setFormDescricaoTipo("");
  };

  const handleSaveTipo = async () => {
    if (!formMedalhaIdTipo || !formNomeTipo || !formImgTipo) {
      enqueueSnackbar("Preencha todos os campos obrigatórios", { variant: "warning" });
      return;
    }

    try {
      setLoading(true);

      if (editingTipo) {
        await api.put(`/medalhas/tipos/${editingTipo.id}`, {
          tipo: formNomeTipo,
          img: formImgTipo,
          descricao: formDescricaoTipo,
        });
        enqueueSnackbar("Tipo de medalha atualizado com sucesso", { variant: "success" });
      } else {
        await api.post("/medalhas/tipos", {
          idMedalha: formMedalhaIdTipo,
          tipo: formNomeTipo,
          img: formImgTipo,
          descricao: formDescricaoTipo,
        });
        enqueueSnackbar("Tipo de medalha criado com sucesso", { variant: "success" });
      }

      closeTipoModal();
      loadMedalhaTipos();
    } catch (error: any) {
      enqueueSnackbar(
        error.response?.data?.message || "Erro ao salvar tipo de medalha",
        { variant: "error" }
      );
    } finally {
      setLoading(false);
    }
  };

  const openExcluirTipoModal = (id: string, nome: string) => {
    setTipoToDelete({ id, tipo: nome });
    setIsExcluirTipoModalOpen(true);
  };

  const closeExcluirTipoModal = () => {
    setIsExcluirTipoModalOpen(false);
    setTipoToDelete(null);
  };

  const handleDeleteTipo = async () => {
    if (!tipoToDelete) return;

    try {
      await api.delete(`/medalhas/tipos/${tipoToDelete.id}`);
      enqueueSnackbar("Tipo de medalha excluído com sucesso", { variant: "success" });
      closeExcluirTipoModal();
      loadMedalhaTipos();
    } catch (error: any) {
      enqueueSnackbar(
        error.response?.data?.message || "Erro ao excluir tipo de medalha",
        { variant: "error" }
      );
    }
  };

  // Atribuir Medalha
  const openAtribuirModal = () => {
    setSelectedMilitarId("");
    setSelectedMilitarNick("");
    setSelectedMedalhaId("");
    setSelectedTipoId("");
    setTiposDisponiveis([]);
    setIsAtribuirModalOpen(true);
  };

  const closeAtribuirModal = () => {
    setIsAtribuirModalOpen(false);
    setSelectedMilitarId("");
    setSelectedMilitarNick("");
    setSelectedMedalhaId("");
    setSelectedTipoId("");
    setTiposDisponiveis([]);
  };

  const handleAtribuirMedalha = async () => {
    if (!selectedMilitarId || !selectedTipoId) {
      enqueueSnackbar("Preencha todos os campos", { variant: "warning" });
      return;
    }

    try {
      setLoading(true);
      await api.post("/medalhas/atribuir", {
        idMilitar: selectedMilitarId,
        idMedalhaTipo: selectedTipoId,
      });
      enqueueSnackbar("Medalha atribuída com sucesso", { variant: "success" });
      closeAtribuirModal();
      loadMedalhasComMilitares();
    } catch (error: any) {
      enqueueSnackbar(
        error.response?.data?.message || "Erro ao atribuir medalha",
        { variant: "error" }
      );
    } finally {
      setLoading(false);
    }
  };

  // Remover Medalha do Militar
  const openRemoverModal = (
    militarMedalhaId: string,
    militarNick: string,
    medalhaNome: string
  ) => {
    setMilitarMedalhaToRemove({
      id: militarMedalhaId,
      militarNick,
      medalhaNome,
    });
    setIsRemoverModalOpen(true);
  };

  const closeRemoverModal = () => {
    setIsRemoverModalOpen(false);
    setMilitarMedalhaToRemove(null);
  };

  const handleRemoverMedalha = async () => {
    if (!militarMedalhaToRemove) return;

    try {
      setLoading(true);
      await api.delete(`/medalhas/militar/${militarMedalhaToRemove.id}`);
      enqueueSnackbar("Medalha removida com sucesso", { variant: "success" });
      closeRemoverModal();
      loadMedalhasComMilitares();
    } catch (error: any) {
      enqueueSnackbar(
        error.response?.data?.message || "Erro ao remover medalha",
        { variant: "error" }
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="border-b border-gray-200 dark:border-border pb-4">
        <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
          <Medal className="h-6 w-6 text-[oklch(0.45_0.15_142)]" />
          Medalhas
        </h1>
        <p className="text-muted-foreground mt-2">
          Gerencie as medalhas, seus tipos e atribuições
        </p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b">
        <button
          className={`px-4 py-2 font-medium transition-colors ${
            activeTab === "medalhas"
              ? "border-b-2 border-[oklch(0.45_0.15_142)] text-[oklch(0.45_0.15_142)]"
              : "text-muted-foreground hover:text-foreground"
          }`}
          onClick={() => setActiveTab("medalhas")}
        >
          Medalhas
        </button>
        <button
          className={`px-4 py-2 font-medium transition-colors ${
            activeTab === "tipos"
              ? "border-b-2 border-[oklch(0.45_0.15_142)] text-[oklch(0.45_0.15_142)]"
              : "text-muted-foreground hover:text-foreground"
          }`}
          onClick={() => setActiveTab("tipos")}
        >
          Tipos de Medalhas
        </button>
      </div>

      {/* Tab: Medalhas */}
      {activeTab === "medalhas" && (
        <div className="space-y-6">
          {/* Filtros */}
          <Card className="border-l-4 border-l-[oklch(0.45_0.15_142)]">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2 text-[oklch(0.35_0.15_142)]">
                    <Search className="w-5 h-5" />
                    Buscar Medalhas e Militares
                  </CardTitle>
                  <CardDescription>
                    Filtrar por medalha ou nick do militar
                  </CardDescription>
                </div>
                {hasPermission(PERMISSOES.ATRIBUIR_MEDALHA) && (
                  <Button
                    onClick={openAtribuirModal}
                    className="bg-[oklch(0.45_0.15_142)] hover:bg-[oklch(0.40_0.15_142)] text-white flex items-center gap-2"
                  >
                    <Plus className="w-4 h-4" />
                    Atribuir Medalha
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSearchMedalhas} className="flex gap-4">
                <div className="flex-1">
                  <Label htmlFor="search-medalha">Medalha</Label>
                  <InputText
                    id="search-medalha"
                    name="search-medalha"
                    title=""
                    placeholder="Digite o nome da medalha..."
                    value={searchMedalha}
                    onChange={(e) => setSearchMedalha(e.target.value)}
                    className="mt-1"
                  />
                </div>
                <div className="flex-1">
                  <Label htmlFor="search-nick-medalha">Nick do Militar</Label>
                  <InputText
                    id="search-nick-medalha"
                    name="search-nick-medalha"
                    title=""
                    placeholder="Digite o nick do militar..."
                    value={searchNick}
                    onChange={(e) => setSearchNick(e.target.value)}
                    className="mt-1"
                  />
                </div>
                <div className="flex items-end">
                  <Button
                    type="submit"
                    disabled={loading}
                    className="bg-[oklch(0.45_0.15_142)] hover:bg-[oklch(0.40_0.15_142)] text-white"
                  >
                    {loading ? "Buscando..." : "Buscar"}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>

          {/* Resultados */}
          <Card>
            <CardHeader>
              <CardTitle className="text-[oklch(0.35_0.15_142)]">Resultados</CardTitle>
              <CardDescription>
                {medallhasComTipos.length} medalha(s) encontrada(s)
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="text-muted-foreground">Carregando...</div>
                </div>
              ) : medallhasComTipos.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  Nenhuma medalha encontrada
                </div>
              ) : (
                <div className="space-y-6">
                  {medallhasComTipos.map((medalha) => {
                    const totalMilitares = medalha.tipos.reduce(
                      (sum, tipo) => sum + tipo.militares.length,
                      0
                    );

                    return (
                      <div key={medalha.id} className="space-y-3">
                        <div className="flex items-center gap-3 pb-2 border-b-2 border-[oklch(0.45_0.15_142)]">
                          <img
                            src={medalha.img}
                            alt={medalha.medalha}
                            className="w-8 h-8 rounded-full object-cover"
                          />
                          <div className="flex-1">
                            <h3 className="font-semibold text-lg text-[oklch(0.45_0.15_142)]">
                              {medalha.medalha}
                            </h3>
                            {medalha.descricao && (
                              <p className="text-xs text-muted-foreground mt-0.5">
                                {medalha.descricao}
                              </p>
                            )}
                          </div>
                          <Badge variant="outline" className="ml-2">
                            {totalMilitares}{" "}
                            {totalMilitares === 1 ? "militar" : "militares"}
                          </Badge>
                        </div>

                        {totalMilitares === 0 ? (
                          <div className="text-sm text-muted-foreground ml-11">
                            Nenhum militar com esta medalha
                          </div>
                        ) : (
                          <div className="ml-4 space-y-4">
                            {medalha.tipos
                              .filter((tipo) => tipo.militares.length > 0)
                              .map((tipo) => (
                                <div key={tipo.id}>
                                  <div className="flex items-center gap-2 mb-2">
                                    <img
                                      src={tipo.img}
                                      alt={tipo.tipo}
                                      className="w-5 h-5 rounded-full object-cover"
                                    />
                                    <h4 className="font-semibold text-[oklch(0.45_0.15_142)]">
                                      {tipo.tipo}
                                    </h4>
                                    <Badge className="bg-green-100 text-green-800 border-green-200 dark:bg-green-900/40 dark:text-green-300 dark:border-green-700">
                                      {tipo.militares.length}
                                    </Badge>
                                  </div>
                                  <div className="space-y-2 ml-6">
                                    {tipo.militares.map((mm) => (
                                      <div
                                        key={mm.id}
                                        className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg"
                                      >
                                        <img
                                          src={`http://www.habbo.com.br/habbo-imaging/avatarimage?&user=${mm.militar.nick}&action=std&direction=5&head_direction=2&img_format=png&gesture=std&headonly=1&size=l`}
                                          alt={mm.militar.nick}
                                          className="w-12 h-12 rounded-full object-cover"
                                          onError={(e) => {
                                            e.currentTarget.style.display = "none";
                                          }}
                                        />
                                        <div className="flex-1">
                                          <div className="font-medium">
                                            {mm.militar.patente.abreviacao} {mm.militar.nick}
                                          </div>
                                          <div className="text-xs text-muted-foreground">
                                            Por: {mm.militarPromoveu.patente.abreviacao}{" "}
                                            {mm.militarPromoveu.nick}
                                          </div>
                                        </div>
                                        {hasPermission(PERMISSOES.REMOVER_MEDALHA) && (
                                          <Button
                                            size="sm"
                                            variant="ghost"
                                            onClick={() =>
                                              openRemoverModal(
                                                mm.id,
                                                mm.militar.nick,
                                                `${medalha.medalha} - ${tipo.tipo}`
                                              )
                                            }
                                            className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:text-red-300 dark:hover:bg-red-900/20"
                                          >
                                            <Trash2 className="w-4 h-4" />
                                          </Button>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tab: Tipos de Medalhas */}
      {activeTab === "tipos" && (
        <div className="space-y-6">
          {/* Busca e Ações */}
          <Card className="border-l-4 border-l-[oklch(0.45_0.15_142)]">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2 text-[oklch(0.35_0.15_142)]">
                    <Search className="w-5 h-5" />
                    Gerenciar Medalhas e Tipos
                  </CardTitle>
                  <CardDescription>
                    Buscar e gerenciar medalhas e seus tipos
                  </CardDescription>
                </div>
                <div className="flex gap-2">
                  {hasPermission(PERMISSOES.CRIAR_MEDALHA) && (
                    <Button
                      onClick={openCreateMedalhaModal}
                      className="bg-[oklch(0.45_0.15_142)] hover:bg-[oklch(0.40_0.15_142)] text-white flex items-center gap-2"
                    >
                      <Plus className="w-4 h-4" />
                      Nova Medalha
                    </Button>
                  )}
                  {hasPermission(PERMISSOES.CRIAR_MEDALHA_TIPO) && (
                    <Button
                      onClick={openCreateTipoModal}
                      className="bg-[oklch(0.45_0.15_142)] hover:bg-[oklch(0.40_0.15_142)] text-white flex items-center gap-2"
                    >
                      <Plus className="w-4 h-4" />
                      Novo Tipo
                    </Button>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <InputText
                id="search-tipos"
                name="search-tipos"
                title=""
                placeholder="Buscar por medalha ou tipo..."
                value={searchTipo}
                onChange={(e) => setSearchTipo(e.target.value)}
              />
            </CardContent>
          </Card>

          {/* Lista de Medalhas */}
          <Card>
            <CardHeader>
              <CardTitle className="text-[oklch(0.35_0.15_142)]">Medalhas</CardTitle>
              <CardDescription>
                {medalhas.length} medalha(s) cadastrada(s)
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="text-muted-foreground">Carregando...</div>
                </div>
              ) : medalhas.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  Nenhuma medalha cadastrada
                </div>
              ) : (
                <div className="space-y-2">
                  {medalhas.map((medalha, index) => (
                    <div
                      key={medalha.id}
                      className="flex items-center gap-3 p-3 border border-border rounded-lg hover:bg-muted/50 transition-colors"
                    >
                      <div className="flex flex-col gap-1 mr-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => moveMedalha(index, "up")}
                          disabled={index === 0}
                          className="h-6 w-6 p-0"
                        >
                          <ArrowUp className="h-3 w-3" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => moveMedalha(index, "down")}
                          disabled={index === medalhas.length - 1}
                          className="h-6 w-6 p-0"
                        >
                          <ArrowDown className="h-3 w-3" />
                        </Button>
                      </div>

                      <div className="flex items-center gap-4 flex-1">
                        <img
                          src={medalha.img}
                          alt={medalha.medalha}
                          className="w-10 h-10 rounded-full object-cover"
                        />
                        <div>
                          <h3 className="font-semibold text-foreground">
                            {medalha.medalha}
                          </h3>
                          {medalha.descricao && (
                            <p className="text-sm text-muted-foreground">
                              {medalha.descricao}
                            </p>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        {hasPermission(PERMISSOES.EDITAR_MEDALHA) && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openEditMedalhaModal(medalha)}
                            className="text-[oklch(0.45_0.15_142)] border-[oklch(0.45_0.15_142)]/20 hover:bg-[oklch(0.45_0.15_142)]/10"
                          >
                            <Edit className="w-4 h-4 mr-1" />
                            Editar
                          </Button>
                        )}
                        {hasPermission(PERMISSOES.EXCLUIR_MEDALHA) && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openExcluirMedalhaModal(medalha.id, medalha.medalha)}
                            className="text-red-600 border-red-200 hover:bg-red-50 dark:text-red-400 dark:border-red-700 dark:hover:bg-red-900/20"
                          >
                            <Trash2 className="w-4 h-4 mr-1" />
                            Excluir
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Lista de Tipos */}
          <Card>
            <CardHeader>
              <CardTitle className="text-[oklch(0.35_0.15_142)]">Tipos de Medalhas</CardTitle>
              <CardDescription>
                {medalhaTipos.length} tipo(s) cadastrado(s)
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="text-muted-foreground">Carregando...</div>
                </div>
              ) : filteredTipos.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  Nenhum tipo encontrado
                </div>
              ) : (
                <div className="space-y-2">
                  {filteredTipos.map((tipo) => (
                    <div
                      key={tipo.id}
                      className="flex items-center gap-3 p-3 border border-border rounded-lg hover:bg-muted/50 transition-colors"
                    >
                      <div className="flex items-center gap-4 flex-1">
                        <img
                          src={tipo.img}
                          alt={tipo.tipo}
                          className="w-8 h-8 rounded-full object-cover"
                        />
                        <div>
                          <h3 className="font-semibold text-foreground">
                            {tipo.medalha.medalha} - {tipo.tipo}
                          </h3>
                          {tipo.descricao && (
                            <p className="text-sm text-muted-foreground">
                              {tipo.descricao}
                            </p>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        {hasPermission(PERMISSOES.EDITAR_MEDALHA_TIPO) && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openEditTipoModal(tipo)}
                            className="text-[oklch(0.45_0.15_142)] border-[oklch(0.45_0.15_142)]/20 hover:bg-[oklch(0.45_0.15_142)]/10"
                          >
                            <Edit className="w-4 h-4 mr-1" />
                            Editar
                          </Button>
                        )}
                        {hasPermission(PERMISSOES.EXCLUIR_MEDALHA_TIPO) && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openExcluirTipoModal(tipo.id, tipo.tipo)}
                            className="text-red-600 border-red-200 hover:bg-red-50 dark:text-red-400 dark:border-red-700 dark:hover:bg-red-900/20"
                          >
                            <Trash2 className="w-4 h-4 mr-1" />
                            Excluir
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Modal: Criar/Editar Medalha */}
      {isModalMedalhaOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle>
                {editingMedalha ? "Editar Medalha" : "Nova Medalha"}
              </CardTitle>
              <CardDescription>
                {editingMedalha
                  ? "Atualize os dados da medalha"
                  : "Crie uma nova medalha"}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Input
                title="Nome da Medalha"
                value={formNomeMedalha}
                onChange={(e) => setFormNomeMedalha(e.target.value)}
                placeholder="Ex: Mérito Operacional"
              />
              <Input
                title="URL da Imagem"
                value={formImgMedalha}
                onChange={(e) => setFormImgMedalha(e.target.value)}
                placeholder="https://..."
              />
              <TextArea
                title="Descrição"
                value={formDescricaoMedalha}
                onChange={(e) => setFormDescricaoMedalha(e.target.value)}
                placeholder="Descrição da medalha (opcional)"
                rows={3}
              />
              <div className="flex gap-2">
                <Button
                  onClick={handleSaveMedalha}
                  disabled={loading}
                  className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                >
                  {editingMedalha ? "Atualizar" : "Criar"}
                </Button>
                <Button onClick={closeMedalhaModal} variant="outline">
                  Cancelar
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Modal: Criar/Editar Tipo */}
      {isModalTipoOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle>
                {editingTipo ? "Editar Tipo de Medalha" : "Novo Tipo de Medalha"}
              </CardTitle>
              <CardDescription>
                {editingTipo
                  ? "Atualize os dados do tipo"
                  : "Crie um novo tipo para a medalha"}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <SelectSingle
                title="Medalha"
                value={formMedalhaIdTipo}
                onChange={(e) => setFormMedalhaIdTipo(e.target.value)}
                disabled={!!editingTipo}
                required
              >
                <option value="">Selecione a medalha</option>
                {medalhas.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.medalha}
                  </option>
                ))}
              </SelectSingle>
              <Input
                title="Nome do Tipo"
                value={formNomeTipo}
                onChange={(e) => setFormNomeTipo(e.target.value)}
                placeholder="Ex: Ouro, Prata, Bronze..."
              />
              <Input
                title="URL da Imagem"
                value={formImgTipo}
                onChange={(e) => setFormImgTipo(e.target.value)}
                placeholder="https://..."
              />
              <TextArea
                title="Descrição"
                value={formDescricaoTipo}
                onChange={(e) => setFormDescricaoTipo(e.target.value)}
                placeholder="Descrição do tipo (opcional)"
                rows={3}
              />
              <div className="flex gap-2">
                <Button
                  onClick={handleSaveTipo}
                  disabled={loading}
                  className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                >
                  {editingTipo ? "Atualizar" : "Criar"}
                </Button>
                <Button onClick={closeTipoModal} variant="outline">
                  Cancelar
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Modal: Atribuir Medalha */}
      {isAtribuirModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-[oklch(0.35_0.15_142)]">
                <Medal className="w-5 h-5" />
                Atribuir Medalha a Militar
              </CardTitle>
              <CardDescription>
                Selecione o militar, a medalha e o tipo
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <MilitarSelectField
                label="Militar"
                value={selectedMilitarId}
                onChange={(id, nick) => {
                  setSelectedMilitarId(id);
                  setSelectedMilitarNick(nick);
                }}
                placeholder="Digite o nome do militar..."
                required
              />

              <SelectSingle
                title="Medalha"
                value={selectedMedalhaId}
                onChange={(e) => setSelectedMedalhaId(e.target.value)}
                required
              >
                <option value="">Selecione a medalha</option>
                {medalhas.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.medalha}
                  </option>
                ))}
              </SelectSingle>

              <SelectSingle
                title="Tipo"
                value={selectedTipoId}
                onChange={(e) => setSelectedTipoId(e.target.value)}
                disabled={!selectedMedalhaId || tiposDisponiveis.length === 0}
                required
              >
                <option value="">
                  {selectedMedalhaId
                    ? "Selecione o tipo..."
                    : "Selecione uma medalha primeiro"}
                </option>
                {tiposDisponiveis.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.tipo}
                  </option>
                ))}
              </SelectSingle>
              {selectedMedalhaId && tiposDisponiveis.length === 0 && (
                <p className="text-xs text-orange-600 mt-1">
                  Esta medalha não possui tipos cadastrados
                </p>
              )}

              <div className="flex gap-2">
                <Button
                  onClick={handleAtribuirMedalha}
                  disabled={loading}
                  className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                >
                  {loading ? "Atribuindo..." : "Atribuir"}
                </Button>
                <Button onClick={closeAtribuirModal} variant="outline">
                  Cancelar
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Modal: Remover Medalha */}
      {isRemoverModalOpen && militarMedalhaToRemove && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-red-600">
                <Trash2 className="w-5 h-5" />
                Remover Medalha
              </CardTitle>
              <CardDescription>
                Esta ação não pode ser desfeita
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 dark:bg-red-950/30 dark:border-red-800">
                <p className="text-sm text-foreground">
                  Tem certeza que deseja remover a medalha{" "}
                  <span className="font-semibold text-green-700 dark:text-green-400">
                    {militarMedalhaToRemove.medalhaNome}
                  </span>{" "}
                  do militar{" "}
                  <span className="font-semibold">
                    {militarMedalhaToRemove.militarNick}
                  </span>
                  ?
                </p>
              </div>

              <div className="flex gap-2">
                <Button
                  onClick={handleRemoverMedalha}
                  disabled={loading}
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                >
                  {loading ? "Removendo..." : "Sim, Remover"}
                </Button>
                <Button onClick={closeRemoverModal} variant="outline">
                  Cancelar
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Modal: Excluir Medalha */}
      {isExcluirMedalhaModalOpen && medalhaToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-red-600">
                <Trash2 className="w-5 h-5" />
                Excluir Medalha
              </CardTitle>
              <CardDescription>
                Esta ação não pode ser desfeita
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 dark:bg-red-950/30 dark:border-red-800">
                <p className="text-sm text-foreground">
                  Tem certeza que deseja excluir permanentemente a medalha{" "}
                  <span className="font-semibold text-green-700 dark:text-green-400">
                    {medalhaToDelete.medalha}
                  </span>
                  ?
                </p>
                <p className="text-xs text-muted-foreground mt-2">
                  Todos os tipos e militares que possuem esta medalha terão ela removida.
                </p>
              </div>

              <div className="flex gap-2">
                <Button
                  onClick={handleDeleteMedalha}
                  disabled={loading}
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                >
                  {loading ? "Excluindo..." : "Sim, Excluir"}
                </Button>
                <Button onClick={closeExcluirMedalhaModal} variant="outline">
                  Cancelar
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Modal: Excluir Tipo */}
      {isExcluirTipoModalOpen && tipoToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-red-600">
                <Trash2 className="w-5 h-5" />
                Excluir Tipo de Medalha
              </CardTitle>
              <CardDescription>
                Esta ação não pode ser desfeita
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 dark:bg-red-950/30 dark:border-red-800">
                <p className="text-sm text-foreground">
                  Tem certeza que deseja excluir permanentemente o tipo{" "}
                  <span className="font-semibold text-green-700 dark:text-green-400">
                    {tipoToDelete.tipo}
                  </span>
                  ?
                </p>
                <p className="text-xs text-muted-foreground mt-2">
                  Todos os militares que possuem este tipo terão ele removido.
                </p>
              </div>

              <div className="flex gap-2">
                <Button
                  onClick={handleDeleteTipo}
                  disabled={loading}
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                >
                  {loading ? "Excluindo..." : "Sim, Excluir"}
                </Button>
                <Button onClick={closeExcluirTipoModal} variant="outline">
                  Cancelar
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
